"use client";

import { unwrapResult } from "@reduxjs/toolkit";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import FadeAnimation from "@/components/atoms/animation/fade-animation";
import TransformAnimation from "@/components/atoms/animation/transform-animation";
import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import Styles from "@/components/molecules/loading-overlay/loading.module.css";
import UnSavedChangesWarningMessage from "@/components/organisms/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withApplication from "@/components/with-application";
import {
  Family,
  QuestionFive,
  QuestionFour,
  QuestionSix,
  Quote,
  QuoteEdit,
  RenewalPage,
  SingleParentFamily,
  TravellerEdit,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import { PRODUCT_DOMAIN } from "@/constants";
import useGtmPopup from "@/hooks/gtm/use-gtm-popup";
import useGtmQuestionSet from "@/hooks/gtm/use-gtm-question-set";
import { useStickyFooter } from "@/hooks/use-media-query/use-media-query-enhanced";
import useReplaceRouterPath from "@/hooks/use-replace-router-path/use-replace-router-path";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useQuestionsState } from "@/providers/questions-state-provider";
import { useAppDispatch } from "@/store/hook";
import {
  saveQuestionDataBFF,
  sessionCheck,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState } from "@/store/store";
import { PartyTypeContainerProps } from "@/types/PartyType";
import { QuotePayload } from "@/types/quote/quote-data";
import { TripDetails } from "@/types/TripDetails";
import selectLoationHookFlash, {
  modifyPayloadSave,
} from "@/utils/common.utils";
import {
  getRouterPathBackButton,
  getRouterPathMainButton,
} from "@/utils/page-routing.utils";
import { findAnswerById, findIdByPartyValue } from "@/utils/party-type.utils";
import { handleScrollToRef } from "@/utils/scrollUtils";

import FamilyTypeModal from "./family-type-modal";
import ActionButton from "../../action-button/action-button";
import ConfirmationsModal from "../../quote-section/edit-quote-section/confirmation-modal";
import editQuoteStyle from "../../quote-section/edit-quote-section/edit-quote.module.css";
import BackToRenewal from "../../renewals/back-to-renewal/back-to-renewal";

const initialPayload: QuotePayload = {
  type: "IntermediateData",
  channel: "ONLINE",
  attributes: {
    domain: PRODUCT_DOMAIN,
    lastVisitedPage: "test",
  },
};

function PartyTypeContainer({
  buttonDisable,
  usagePage,
  answerArray,
  selectedParty,
  pageContent,
  formRef,
  ctaRef,
  application = "quote",
}: Readonly<PartyTypeContainerProps>) {
  const {
    questionText = "Question Text",
    ctaButtonText = "Button Text",
    familyModalTitleText,
    familyModalSubTextEnable,
    familyModalSubText,
    dropDownPlaceHolder,
    confirmButtonText,
    backButtonText,
    cancelEditingBtnText = "Cancel Editing",
    editPartyTypePlaceholder,
    backToRenewalsText,
  } = pageContent;
  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [editModal, setEditModal] = useState(false);
  const editPage = searchParams.get(TravellerEdit);

  const { setSelectedState } = useQuestionsState();
  const [payload, modifyPayload] = useState<QuotePayload>(initialPayload);
  const [isSubmitBtnClicked, setSubmitBtnClicked] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFamily, setSelectedFamily] = useState<number | null>(null);
  const { resetQuestionData } = useResetStateTravelerEdit();
  const pathname = usePathname();
  useReplaceRouterPath(pathname);
  const isStickyFooter = useStickyFooter();

  const [isDisable, setIsDisable] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const actionButtonRef = useRef<HTMLDivElement | null>(null);

  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);

  const quoteResponse = useSelector((state: RootState) => state.quote);
  const { loading } = quoteResponse;
  const { tripDetails } = quoteResponse?.attributes || undefined;

  const accessSelectedAnswer = (answerNo: number) => {
    const selectedPartyUser = findAnswerById(answerArray, answerNo)?.value;

    // de select the previous selected answer
    if (
      selectedPartyUser === selectedParty ||
      (selectedPartyUser === Family && selectedParty === SingleParentFamily)
    ) {
      setSelectedFamily(null);
      setSelectedState(0);
      return;
    }

    if (selectedPartyUser === Family) {
      setSelectedFamily(null);
      setIsModalOpen(true);
    } else {
      const tripDetailsModified: TripDetails = {
        ...tripDetails,
        partyType: selectedPartyUser,
      };

      modifyPayload(modifyPayloadSave(quoteResponse, tripDetailsModified));

      setSelectedState(selectedPartyUser);
    }
  };

  useEffect(() => {
    if (selectedParty && tripDetails) {
      const tripDetailsModified: TripDetails = {
        ...tripDetails,
        partyType: selectedParty,
      };
      modifyPayload(modifyPayloadSave(quoteResponse, tripDetailsModified));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDetails, selectedParty]);

  useEffect(() => {
    if (selectedParty) {
      if (selectedParty === Family) {
        setSelectedFamily(2);
      } else if (selectedParty === SingleParentFamily) {
        setSelectedFamily(1);
      }
    }
  }, [selectedFamily, selectedParty]);

  const handleModalButtonClick = (
    buttonClick: boolean,
    selectedAnswer: number | null
  ) => {
    setIsModalOpen(!isModalOpen);

    setIsDisable(true);
    setTimeout(() => {
      setIsDisable(false);
    }, 100);

    if (buttonClick && selectedAnswer) {
      // const familyType = findAnswerFamilyById(selectedAnswer)?.value;
      // change the logic here
      const partType = selectedAnswer === 1 ? SingleParentFamily : Family;
      const tripDetailsModified: TripDetails = {
        ...tripDetails,
        ...{ partType },
      };

      modifyPayload(modifyPayloadSave(quoteResponse, tripDetailsModified));
      setSelectedState(partType);
      setSelectedFamily(selectedAnswer);
    } else {
      setSelectedFamily(null);
    }
    // else if (buttonClick && selectedNumberOfParents === 2) {
    //   // change the logic here
    //   setSelectedAnswer(Family);
    // }
  };

  const handleSubmitButton = async () => {
    if (!buttonDisable) {
      setSubmitBtnClicked(true);
      setButtonLoading(true);
      try {
        dispatch(setQuestionData(payload as QuotePayload));
        if (!quoteResponse?.referenceId) {
          const questionData = await dispatch(
            saveQuestionDataBFF({
              payload: { ...payload, viewPage: Routes.CoverFor },
              dispatch,
              router,
            })
          );
          const originalPromiseResult = unwrapResult(questionData);
          if (originalPromiseResult) {
            router.push(getRouterPathMainButton(QuestionSix));
          }
        } else {
          dispatch(sessionCheck({ dispatch, router }));
          let path = getRouterPathMainButton(QuestionSix);
          if (editPage) {
            path = `${path}/?${TravellerEdit}=true`;
          }
          router.push(path);
        }
        setButtonLoading(false);
      } catch {
        setButtonLoading(false);
      }
    }
  };

  const handleBackButton = () => {
    if (application === RenewalPage && editPage) {
      setIsOpenUnsavedChangesModal(true);
    } else if (application === RenewalPage) {
      router.push(getRouterPathBackButton(QuestionFive));
    } else {
      router.push(getRouterPathBackButton(QuestionSix));
    }
  };
  const toLocationData = selectLoationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionSix,
    tripType: payload?.attributes?.tripDetails?.policyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: payload?.attributes?.tripDetails?.startDate || undefined,
    coverEndDate: payload?.attributes?.tripDetails?.endDate || undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation || undefined,
    travelGroupType: payload?.attributes?.tripDetails?.partyType || undefined,
    isSubmitBtnClicked,
  });

  useGtmPopup({
    tripType: QuestionSix,
    isPopupOpen: isModalOpen,
    popupName: "Family Travel Popup",
  });

  const handleCancelEdit = () => {
    setEditModal(true);
  };

  const handleUnSavedWarningMessageContinueBtn = () => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  };

  const questionLoading = () => {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
        }}
      >
        <div className={`${Styles.loader}`} />
      </div>
    );
  };

  const renderQuestionOneSection = () => {
    const selectedPartyType =
      selectedParty === SingleParentFamily
        ? findIdByPartyValue(answerArray, Family)?.id
        : findIdByPartyValue(answerArray, selectedParty)?.id;

    return (
      <div className="" data-testid="party-type-support-content">
        {" "}
        {editPage && application !== RenewalPage && (
          <div>
            <LabelButtonWrapper
              hierarchy="tertiary"
              label={cancelEditingBtnText}
              id="cancel-edit-party-type"
              className={`${editQuoteStyle.cancelEditButton}`}
              onClick={handleCancelEdit}
            />
            <ConfirmationsModal
              isOpen={editModal}
              handleConfirmBtnClick={() => {
                router.push(`/${Quote}/?${QuoteEdit}=true`);
                resetQuestionData();
              }}
              handleCancelmBtnClick={() => {
                setEditModal(false);
              }}
            />
          </div>
        )}
        <h1
          className=" font-header-font lg:text-[39px] md:text-3xl text-[26px] lg:leading-[44.85px] 
                md:leading-[34.1px] leading-[29.9px] text-text-title md:text-left text-center"
        >
          {questionText}
        </h1>
        {application === "renewal" && editPartyTypePlaceholder?.["0"] && (
          <div className="mt-spacing-l">
            <RichText
              richText={editPartyTypePlaceholder["0"].content}
              className="text-text-on-primary font-proxima-nova text-xs font-normal leading-[27px]"
            />
          </div>
        )}
        <ButtonGroup
          setSelectedAnswer={accessSelectedAnswer}
          answerArray={answerArray}
          location={QuestionFive}
          selectedAnswer={selectedPartyType}
          type="party-type"
          isDisable={isDisable}
          onOptionClick={(id, dataTestId) => {
            const buttonElement = document.querySelector(`#${dataTestId}`);
            if (
              buttonElement?.classList.contains(
                "componnt_lib_toggle_btn_inactive"
              )
            ) {
              handleScrollToRef(actionButtonRef, !isStickyFooter);
            }
          }}
        />
        <FamilyTypeModal
          selectedAnswer={selectedFamily}
          isOpen={isModalOpen}
          modalConfirmBtnClick={handleModalButtonClick}
          familyModalTitleText={familyModalTitleText}
          familyModalSubTextEnable={familyModalSubTextEnable}
          familyModalSubText={familyModalSubText}
          dropDownPlaceHolder={dropDownPlaceHolder}
          confirmButtonText={confirmButtonText}
          backButtonText={backButtonText}
        />
      </div>
    );
  };

  const editFinishStyle =
    editPage && application !== RenewalPage
      ? editQuoteStyle.editFinishButton
      : "";

  return (
    <div
      data-testid="party-type-test-id"
      className={`rounded-radius-xxl 
      bg-surface-neutral-option-1 rounded-b-radius-xxl  
           md:py-spacing-xxl py-spacing-m px-spacing-m h-full`}
    >
      <div className="lg:w-[414px] md:w-[400px] w-full mx-auto relative h-full flex flex-col justify-between">
        {application === RenewalPage && editPage && (
          <UnSavedChangesWarningMessage
            isOpen={isOpenUnsavedChangesModal}
            onContinueClick={handleUnSavedWarningMessageContinueBtn}
            setIsOpen={setIsOpenUnsavedChangesModal}
          />
        )}

        {application === "renewal" && editPage && !loading && (
          <div>
            <BackToRenewal
              label={backToRenewalsText ?? "Back to renewal"}
              className="pb-spacing-m md:pb-[0px] 
        top-[-2px] !px-[0px] mb-spacing-l md:!mb-spacing-l lg:mx-[0px]"
              onClick={() => setIsOpenUnsavedChangesModal(true)}
            />
          </div>
        )}

        <TransformAnimation animationRef={formRef} currentPage={usagePage}>
          {application === RenewalPage && loading ? (
            <div className="relative">{questionLoading()}</div>
          ) : (
            renderQuestionOneSection()
          )}
        </TransformAnimation>

        <FadeAnimation animationRef={ctaRef}>
          <div ref={actionButtonRef} className={`${editFinishStyle}`}>
            <ActionButton
              page={usagePage}
              ctaButtonText={ctaButtonText}
              disableMain={
                buttonDisable || (application === RenewalPage && loading)
              }
              showBackBtn={!editPage || application === RenewalPage}
              isLoading={buttonLoading}
              submitButtonClick={handleSubmitButton}
              backButtonClick={handleBackButton}
            />
          </div>
        </FadeAnimation>
      </div>
    </div>
  );
}
export default withApplication(PartyTypeContainer);
